'use client';

import { useState, useEffect, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

interface CatalogProduct {
  asin: string;
  title: string;
  brand: string;
  image: string | null;
  marketplaces: string[];
}

export function ProductSlider() {
  const locale = useLocale();
  const t = useTranslations('shop');
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/api/public/catalog?limit=10')
      .then((r) => r.json())
      .then((data) => setProducts(data.products || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="pub-card" style={{ padding: '14px 16px' }}>
        <div className="flex items-center gap-2 mb-3">
          <div style={{ height: 12, width: 80, background: 'var(--pub-surface-3)', borderRadius: 6 }} />
        </div>
        <div className="flex gap-2.5 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex-shrink-0"
              style={{
                width: 130,
                height: 160,
                borderRadius: 12,
                background: 'var(--pub-surface-3)',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <div className="pub-card" style={{ padding: '14px 16px 16px', overflow: 'hidden' }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--pub-text-muted)',
          }}
        >
          {t('ourProducts')}
        </span>
        <Link
          href={`/${locale}/shop`}
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 10,
            letterSpacing: '0.06em',
            color: 'rgb(245,158,11)',
            textDecoration: 'none',
          }}
        >
          {t('showAll')} &rarr;
        </Link>
      </div>

      {/* Horizontal scroll */}
      <div
        ref={scrollRef}
        className="flex gap-2.5 overflow-x-auto"
        style={{
          margin: '0 -16px',
          padding: '0 16px',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {products.map((product) => (
          <Link
            key={product.asin}
            href={`/${locale}/shop/${product.asin}`}
            className="flex-shrink-0 flex flex-col overflow-hidden transition-transform duration-150 active:scale-[0.97]"
            style={{
              width: 130,
              borderRadius: 12,
              border: '1px solid var(--pub-border)',
              background: 'var(--pub-surface-2)',
              textDecoration: 'none',
              scrollSnapAlign: 'start',
            }}
          >
            {/* Image */}
            <div
              className="flex items-center justify-center"
              style={{
                height: 110,
                background: '#fff',
                borderBottom: '1px solid var(--pub-border)',
              }}
            >
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-contain"
                  style={{ padding: 6 }}
                />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="1.5" width="24" height="24" style={{ opacity: 0.2 }}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              )}
            </div>

            {/* Title */}
            <div
              className="line-clamp-2"
              style={{
                padding: '8px 10px 10px',
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--pub-text-primary)',
                lineHeight: 1.3,
              }}
            >
              {product.title}
            </div>
          </Link>
        ))}

        {/* "See all" card at the end */}
        <Link
          href={`/${locale}/shop`}
          className="flex-shrink-0 flex flex-col items-center justify-center transition-transform duration-150 active:scale-[0.97]"
          style={{
            width: 100,
            minHeight: 150,
            borderRadius: 12,
            border: '1px dashed var(--pub-border)',
            background: 'var(--pub-surface-3)',
            textDecoration: 'none',
            scrollSnapAlign: 'start',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="rgb(245,158,11)" strokeWidth="2" width="20" height="20" style={{ marginBottom: 8 }}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'rgb(245,158,11)',
            }}
          >
            {t('seeAll')}
          </span>
        </Link>
      </div>

      {/* Hide scrollbar */}
      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
